import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { GHAAccountType, getHintRefreshQueue, removeHintRefresh } from '~/services/ConnectionGoogleService';

export const hintRefreshQueueQueryKeys = {
  all: ['hintRefreshQueue'] as const,
  list: (accountType: GHAAccountType) => [...hintRefreshQueueQueryKeys.all, accountType] as const,
};

/**
 * Live view of the hint refresh queue (GR-4819). The queue drains within a hint cycle
 * (2–5 min), so the panel refetches on mount and exposes `refetch` behind a Refresh button
 * rather than polling.
 */
export function useHintRefreshQueue(accountType: GHAAccountType) {
  return useQuery({
    queryKey: hintRefreshQueueQueryKeys.list(accountType),
    queryFn: async ({ signal }) => {
      return getHintRefreshQueue(accountType, { signal });
    },
    refetchOnMount: 'always',
  });
}

/** Remove a pending entry; refreshes the queue view on success (even if the entry already left). */
export function useRemoveHintRefresh(accountType: GHAAccountType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (propertyIds: Array<string>) => {
      return removeHintRefresh(propertyIds, accountType);
    },
    onSettled: () => {
      return queryClient.invalidateQueries({ queryKey: hintRefreshQueueQueryKeys.list(accountType) });
    },
  });
}
